import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { toAmsterdamISO, todayStr, nowTimeStr, afspraakMoment, afspraakIsVerplaatst } from '../../utils/dateUtils.js';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

function fmtDateTime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return format(d, "EEEE d MMMM yyyy, HH:mm", { locale: nl });
  } catch { return isoStr; }
}

function StatusBadge({ status }) {
  const cfg = {
    pending:     { label: t('afspraken.statusPending'),     bg: '#6b7280', color: '#fff' },
    accepted:    { label: t('afspraken.statusAccepted'),    bg: '#dcfce7', color: '#166534' },
    declined:    { label: t('afspraken.statusDeclined'),    bg: '#fee2e2', color: '#991b1b' },
    rescheduled: { label: t('afspraken.statusRescheduled'), bg: '#ffedd5', color: '#9a3412' },
  };
  const { label, bg, color } = cfg[status] || { label: status, bg: '#e5e7eb', color: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      background: bg,
      color,
    }}>
      {label}
    </span>
  );
}

export default function AfsprakenPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [aanvragen, setAanvragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [coachId, setCoachId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState(nowTimeStr());
  const [duration, setDuration] = useState('60');
  const [note, setNote] = useState('');
  const [creditCost, setCreditCost] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [respondingId, setRespondingId] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('appointment_requests')
        .select(`
          id, requested_at, duration_minutes, note, status, credit_cost,
          reschedule_proposed_at, reschedule_note, created_at, lesson_id,
          coach:coach_basic_profiles!appointment_requests_coach_id_fkey(id, full_name),
          location:locations(id, name),
          lesson:group_lessons!appointment_requests_lesson_id_fkey(id, starts_at, is_cancelled)
        `)
        .eq('client_id', user.id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      setAanvragen(data || []);
    } catch (err) {
      setError(t('afspraken.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadFormData() {
    try {
      const [coachRes, locRes, settingsRes] = await Promise.all([
        supabase
          .from('coach_clients')
          .select('coach_id, is_primary, coach:coach_basic_profiles!coach_clients_coach_id_fkey(id, full_name)')
          .eq('client_id', user.id)
          .eq('status', 'active'),
        supabase
          .from('locations')
          .select('id, name')
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('settings')
          .select('value')
          .eq('key', 'pt_credit_cost')
          .maybeSingle(),
      ]);
      const coachList = (coachRes.data || [])
        .map(cc => cc.coach)
        .filter(Boolean)
        .sort((a, b) => {
          const ccA = coachRes.data.find(cc => cc.coach_id === a.id);
          const ccB = coachRes.data.find(cc => cc.coach_id === b.id);
          if (ccA?.is_primary && !ccB?.is_primary) return -1;
          if (!ccA?.is_primary && ccB?.is_primary) return 1;
          return a.full_name.localeCompare(b.full_name);
        });
      setCoaches(coachList);
      setLocations(locRes.data || []);
      if (settingsRes.data?.value) {
        setCreditCost(parseInt(settingsRes.data.value, 10) || 1);
      }
      if (coachList.length > 0 && !coachId) {
        setCoachId(coachList[0].id);
      }
      if (locRes.data?.length === 1) {
        setLocationId(locRes.data[0].id);
      }
    } catch (err) {
      console.error('Error loading form data:', err);
    }
  }

  useEffect(() => {
    load();
    if (user?.id) {
      supabase.from('profiles').select('blocked_at').eq('id', user.id).maybeSingle()
        .then(({ data }) => setIsBlocked(!!data?.blocked_at));
    }
  }, [user?.id]);

  function openModal() {
    setShowModal(true);
    setFormError('');
    loadFormData();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');

    const dur = parseInt(duration, 10);
    if (!coachId) { setFormError(t('afspraken.coachVerplicht')); return; }
    if (!locationId) { setFormError(t('afspraken.locatieVerplicht')); return; }
    if (!dur || dur <= 0) { setFormError(t('afspraken.duurVerplicht')); return; }

    setSaving(true);
    try {
      const requested_at = toAmsterdamISO(date, time);
      const { error: insertErr } = await supabase
        .from('appointment_requests')
        .insert({
          client_id: user.id,
          coach_id: coachId,
          location_id: locationId || null,
          requested_at,
          duration_minutes: dur,
          note: note || null,
          status: 'pending',
          credit_cost: creditCost,
        });
      if (insertErr) throw insertErr;

      showSuccess(t('afspraken.ingediend'));
      setShowModal(false);
      setNote('');
      setDuration('60');
      await load();
    } catch (err) {
      setFormError(t('afspraken.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleRespond(aanvraagId, accepted) {
    setRespondingId(aanvraagId);
    try {
      if (accepted) {
        // Atomaire RPC — bouwt de les, trekt credits af en schrijft in.
        // Een kale status-update hier zou een 'accepted'-aanvraag zonder
        // les/credit-aftrek achterlaten (zie src/services/database.js
        // updateAanvraagStatus voor het app-equivalent).
        const { data: rpcResult, error: rpcErr } = await supabase.rpc('accept_appointment_request', {
          p_aanvraag_id: aanvraagId,
        });
        if (rpcErr) throw rpcErr;
        if (rpcResult?.error) throw new Error(rpcResult.message || rpcResult.error);
      } else {
        const { error: err } = await supabase
          .from('appointment_requests')
          .update({ status: 'declined' })
          .eq('id', aanvraagId);
        if (err) throw err;
      }
      showSuccess(accepted ? t('afspraken.geaccepteerd') : t('afspraken.afgewezen'));
      await load();
    } catch (err) {
      showError(t('afspraken.foutReageren') + err.message);
    } finally {
      setRespondingId(null);
    }
  }

  return (
    <div>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2 className="modal-title">{t('afspraken.nieuweTitel')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              {formError && <div className="error-msg" style={{ marginBottom: 16 }}>{formError}</div>}

              <div className="form-group">
                <label className="form-label">{t('afspraken.coach')} *</label>
                <select className="form-input" value={coachId} onChange={e => setCoachId(e.target.value)}>
                  <option value="">{t('afspraken.selecteerCoach')}</option>
                  {coaches.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('afspraken.locatie')} *</label>
                <select className="form-input" value={locationId} onChange={e => setLocationId(e.target.value)}>
                  <option value="">{t('afspraken.selecteerLocatie')}</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t('afspraken.datum')} *</label>
                  <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} min={todayStr()} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t('afspraken.tijd')} *</label>
                  <input className="form-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">{t('afspraken.duur')} *</label>
                <input className="form-input" type="number" value={duration} onChange={e => setDuration(e.target.value)} min="15" step="15" />
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">{t('afspraken.credits')}</label>
                <input className="form-input" type="number" value={creditCost} readOnly disabled style={{ background: 'var(--color-bg)', cursor: 'not-allowed' }} />
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">{t('afspraken.notitie')}</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder={t('afspraken.notitiePlaceholder')}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)} disabled={saving}>
                  {t('afspraken.annuleren')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('afspraken.bezig') : t('afspraken.indienen')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">{t('afspraken.title')}</h1>
        </div>
        {!isBlocked && (
          <button className="btn btn-primary" onClick={openModal}>
            {t('afspraken.nieuweKnop')}
          </button>
        )}
      </div>

      {isBlocked && (
        <div style={{ background: '#dc262622', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <p style={{ color: '#dc2626', fontWeight: 600, margin: 0, textAlign: 'center' }}>
            {t('clientRooster.accountGeblokkeerd')}
          </p>
        </div>
      )}

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : aanvragen.length === 0 ? (
        <div className="card">
          <p className="text-muted">{t('afspraken.geenAanvragen')}</p>
          <p className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>{t('afspraken.geenAanvragenSub')}</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('afspraken.colDatum')}</th>
                  <th>{t('afspraken.colCoach')}</th>
                  <th>{t('afspraken.colLocatie')}</th>
                  <th>{t('afspraken.colDuur')}</th>
                  <th>{t('afspraken.colCredits')}</th>
                  <th>{t('afspraken.colStatus')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {aanvragen.map(a => (
                  <tr key={a.id}>
                    <td className="font-medium">
                      {fmtDateTime(afspraakMoment(a))}
                      {afspraakIsVerplaatst(a) && (
                        <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-muted)' }}>
                          {t('afspraken.origineelAangevraagd')}: {fmtDateTime(a.requested_at)}
                        </div>
                      )}
                    </td>
                    <td className="text-muted">{a.coach?.full_name || '—'}</td>
                    <td className="text-muted">{a.location?.name || '—'}</td>
                    <td className="text-muted">{a.duration_minutes} min</td>
                    <td className="text-muted">{a.credit_cost}</td>
                    <td><StatusBadge status={a.status} /></td>
                    <td>
                      {a.status === 'rescheduled' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleRespond(a.id, true)}
                            disabled={respondingId === a.id}
                          >
                            {t('afspraken.accepteren')}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: 'var(--color-danger)' }}
                            onClick={() => handleRespond(a.id, false)}
                            disabled={respondingId === a.id}
                          >
                            {t('afspraken.afwijzen')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {aanvragen.some(a => a.status === 'rescheduled') && (
            <div style={{ padding: 16, borderTop: '1px solid var(--color-border)' }}>
              {aanvragen.filter(a => a.status === 'rescheduled').map(a => (
                <div key={a.id} style={{
                  background: '#fff7ed',
                  border: '1px solid #fed7aa',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#9a3412', marginBottom: 4 }}>
                    {t('afspraken.voorstelCoach')}
                  </div>
                  <div style={{ fontSize: 13 }}>{fmtDateTime(a.reschedule_proposed_at)}</div>
                  {a.reschedule_note && (
                    <div style={{ fontSize: 12, color: '#78716c', marginTop: 4 }}>{a.reschedule_note}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
